import { useEffect, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { Button } from "components/ui/Button";
import { Checkbox as CustomCheckbox } from "components/ui/Checkbox";
import { Select } from "components/ui/Select/Select";
import {
  OCF_CORRECTION_REASON_OPTIONS,
  OCF_DEFAULT_CORRECTION_REASON,
} from "features/cdm/constants/correctionReasonOptions";
import { isHighRiskEthnicity } from "features/cdm/ocf/utils/highRiskEthnicity";
import { isPregnancyConditionVisible } from "features/cdm/pp/utils/visibleRegistrationReasons";
import { useEthnicGroups } from "features/patients/hooks/useEthnicGroups";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { resolveEthnicityCode } from "features/patients/utils";
import { useStaffMembers } from "features/practice/hooks/useStaffMembers";
import { onInvalidSubmit } from "utils/reportFormErrors";
import { formatEircode, validateEircode } from "utils/validators";

import style from "../AddOCFReviewDialog/AddOCFReviewDialog.module.css";

const editSchema = z.object({
  consultationDate: z.string().min(1, { message: "Please provide consultation date." }),
  eircode: z
    .string()
    .min(1, { message: "Please provide eircode." })
    .refine(
      (val) => validateEircode(val).isValid,
      (val) => ({ message: validateEircode(val).error || "Invalid Eircode format" })
    ),
  ethnicityCode: z.string().optional(),
  reviewingDoctor: z.string().optional(),
  other: z.boolean().optional(),
  otherText: z.string().optional(),
  currentSmokingStatus: z.boolean(),
  bmi: z.boolean(),
  previousBNP: z.boolean(),
  highRiskEthnicity: z.boolean(),
  historyOfGestationalDiabetes: z.boolean(),
  dyslipidsemia: z.boolean(),
  chronicKidneyDisease: z.boolean(),
  historyOfSevereMentalIllness: z.boolean(),
}).superRefine((data, ctx) => {
  if (data.other && !data.otherText?.trim()) {
    ctx.addIssue({
      code: "custom",
      path: ["otherText"],
      message: "Please provide details for 'Other'.",
    });
  }
});

export const OcfInitialStep = ({ data, onContinue, onCancel, submitDisabled = false }) => {
  const { data: staffMembers } = useStaffMembers();
  const { data: patient } = usePatientData();
  const { data: ethnicGroups } = useEthnicGroups();

  // Editing an already-submitted review is a correction (HL7 OBR.25=C).
  const isCorrection = !!data?.reimbursementMessageControlId;

  const form = useForm({
    resolver: zodResolver(editSchema),
    defaultValues: {
      consultationDate: data?.consultationDate ?? "",
      eircode: data?.eircode ?? "",
      reviewingDoctor: "",
      ethnicityCode: data?.ethnicityCode ?? "",
      other: !!data?.otherText,
      otherText: data?.otherText ?? "",
      currentSmokingStatus: data?.currentSmokingStatus ?? false,
      bmi: data?.bmiGreaterThan30 ?? false,
      previousBNP: data?.previousHighBnp ?? false,
      highRiskEthnicity: data?.highRiskEthnicity ?? false,
      historyOfGestationalDiabetes: data?.historyOfGestationalDiabetes ?? false,
      dyslipidsemia: data?.dyslipidaemia ?? false,
      chronicKidneyDisease: data?.chronicKidneyDisease ?? false,
      historyOfSevereMentalIllness: data?.historyOfSevereMentalIllness ?? false,
    },
  });

  useEffect(() => {
    // On edit keep the eircode submitted with the previous review; only fall
    // back to the patient record for a new review (no saved value).
    if (!data?.eircode && patient?.address?.postCode) {
      form.setValue("eircode", formatEircode(patient.address.postCode) || patient.address.postCode);
    }
  }, [data?.eircode, patient?.address?.postCode, form]);

  useEffect(() => {
    // Prefill ethnicity review-first (its value is a description enum), then fall
    // back to the patient file (a code). Normalise either shape to a code so the
    // Select — which matches by `g.code` — displays it, and the submitted value
    // stays a code (unchanged from the manual-select path).
    if (!ethnicGroups?.length) return;

    const source = data?.ethnicityCode || patient?.ethnicityCode;
    const code = resolveEthnicityCode(source, ethnicGroups);

    if (code) form.setValue("ethnicityCode", code);
  }, [
    data?.ethnicityCode,
    patient?.ethnicityCode,
    ethnicGroups,
    form,
  ]);

  // Drive the `highRiskEthnicity` indicator ("Ethnicity" — PID.22) off the form's
  // live ethnicity Select, not the patient record. React only when the resolved
  // code actually CHANGES, so a manual tick/untick between changes is preserved
  // (CDM-spec.md:1593). Editing a saved review: seed the previous-code ref with
  // the initially-loaded ethnicity so the mount/prefill is NOT treated as a
  // change — the saved `highRiskEthnicity` is kept until the user changes the
  // Select. Only SUBSEQUENT user changes drive the flag.
  const watchedEthnicityCode = form.watch("ethnicityCode");
  const previousEthnicityCodeRef = useRef(null);
  const hasSeededEthnicityRef = useRef(false);

  useEffect(() => {
    if (!ethnicGroups?.length) return;

    if (!hasSeededEthnicityRef.current) {
      hasSeededEthnicityRef.current = true;
      previousEthnicityCodeRef.current = resolveEthnicityCode(
        data?.ethnicityCode || patient?.ethnicityCode,
        ethnicGroups
      );

      return;
    }

    if (watchedEthnicityCode === previousEthnicityCodeRef.current) return;

    previousEthnicityCodeRef.current = watchedEthnicityCode;

    const highRisk = isHighRiskEthnicity(watchedEthnicityCode, ethnicGroups);
    if (form.getValues("highRiskEthnicity") !== highRisk) {
      form.setValue("highRiskEthnicity", highRisk);
    }
  }, [
    watchedEthnicityCode,
    data?.ethnicityCode,
    patient?.ethnicityCode,
    ethnicGroups,
    form,
  ]);

  useEffect(() => {
    if (staffMembers && data?.reviewingDoctor) {
      const match = staffMembers.find(
        (s) => `${s.firstName} ${s.lastName}` === data.reviewingDoctor
      );
      if (match) {
        form.setValue("reviewingDoctor", String(match.id));
      }
    }
  }, [staffMembers, data?.reviewingDoctor, form]);

  const handleEircodeBlur = (event) => {
    const val = event.target.value;

    if (val) {
      form.setValue("eircode", formatEircode(val), { shouldValidate: true });
    }
  };

  const handleFormSubmit = (formData) => {
    // A correction (HL7 OBR.25=C) must hold the original treatment date
    // (GP Contract spec, Enhancement 27). Consultation date is read-only here,
    // so always carry the original reviewDate — never recompute it.
    onContinue({
      reviewDate: data?.reviewDate,
      consultationDate: data?.consultationDate ?? formData.consultationDate,
      ethnicityCode: formData.ethnicityCode,
      eircode: formData.eircode ? formData.eircode.replace(/\s+/g, "") : formData.eircode,
      currentSmokingStatus: formData.currentSmokingStatus,
      bmiGreaterThan30: formData.bmi,
      previousHighBnp: formData.previousBNP,
      highRiskEthnicity: formData.highRiskEthnicity,
      historyOfGestationalDiabetes: formData.historyOfGestationalDiabetes,
      dyslipidaemia: formData.dyslipidsemia,
      chronicKidneyDisease: formData.chronicKidneyDisease,
      historyOfSevereMentalIllness: formData.historyOfSevereMentalIllness,
      otherText: formData.otherText,
    });
  };

  return (
    <FormProvider {...form}>
      <form
        className={style.addReferralForm}
        style={{ borderTop: "none", paddingTop: 0, marginTop: 0, gap: 0 }}
        onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("OcfInitialStep"))}
      >
      <div style={{ backgroundColor: "var(--gray-50)", paddingTop: "24px", paddingBottom: "24px" }}>
        <div
          className={style.inputGroupGrid4Columns}
          style={{
            padding: "0 24px",
            ...(isCorrection && { gridTemplateColumns: "repeat(5, 1fr)" }),
          }}
        >
          {isCorrection && (
            <FormField name="correctionReason">
              <label className={style.addReferralFormLabel}>
                Reason for correction
              </label>
              <Select
                options={OCF_CORRECTION_REASON_OPTIONS}
                value={OCF_DEFAULT_CORRECTION_REASON}
                onChange={() => {}}
                disabled
              />
            </FormField>
          )}
          <FormField name="consultationDate">
            <label className={style.pregnancyFormLabel}>Consultation date</label>
            <input
              {...form.register("consultationDate")}
              type="text"
              className="form-control"
              placeholder="dd-MM-yyyy"
              readOnly
              disabled
              title="A correction keeps the original review date — it cannot be changed."
            />
          </FormField>
          <FormField name="reviewingDoctor">
            <label className={style.addReferralFormLabel}>Reviewing Doctor</label>
            <Select
              options={(staffMembers ?? []).map((staff) => ({
                value: staff.id,
                label: `${staff.firstName} ${staff.lastName}${staff.staffRole ? ` (${staff.staffRole.toLowerCase()})` : ""}${staff.active ? "" : " (inactive)"}`,
              }))}
              value={form.watch("reviewingDoctor") ?? ""}
              onChange={(value) => form.setValue("reviewingDoctor", value, { shouldValidate: true })}
            />
          </FormField>
          <FormField name="ethnicityCode">
            <label className={style.addReferralFormLabel}>Ethnicity</label>
            <Select
              options={(ethnicGroups ?? []).map((group) => group.description)}
              value={ethnicGroups?.find((g) => g.code === form.watch("ethnicityCode"))?.description ?? ""}
              onChange={(value) => {
                const selectedGroup = ethnicGroups?.find((g) => g.description === value);
                form.setValue("ethnicityCode", selectedGroup?.code ?? "", { shouldValidate: true });
              }}
            />
          </FormField>
          <FormField name="eircode">
            <label className={style.pregnancyFormLabel}>Eircode</label>
            <input
              type="text"
              className="form-control"
              {...form.register("eircode")}
              onBlur={handleEircodeBlur}
            />
          </FormField>
        </div>
        <div className={style.indicatorsSection} style={{ paddingLeft: "24px", paddingRight: "24px" }}>
          <h2 style={{ fontSize: "18px", fontWeight: "bold" }}>Indicators for Review</h2>
          <fieldset className={style.medsCheckboxGroup}>
            <div className={style.checkboxWrapper}>
              <label className={style.pregnancyFormCheckboxLabel}>
                <CustomCheckbox
                  checked={form.watch("currentSmokingStatus")}
                  onChange={(event) => form.setValue("currentSmokingStatus", event.target.checked)}
                />
                Current Smoking Status
              </label>
              <label className={style.pregnancyFormCheckboxLabel}>
                <CustomCheckbox
                  checked={form.watch("bmi")}
                  onChange={(event) => form.setValue("bmi", event.target.checked)}
                />
                {"BMI >=30kg/m2"}
              </label>
              <label className={style.pregnancyFormCheckboxLabel}>
                <CustomCheckbox
                  checked={form.watch("previousBNP")}
                  onChange={(event) => form.setValue("previousBNP", event.target.checked)}
                />
                {"Previous BNP >=34pg/ml or NTproBNP >=125pg/ml"}
              </label>
              <label className={style.pregnancyFormCheckboxLabel}>
                <CustomCheckbox
                  checked={form.watch("highRiskEthnicity")}
                  onChange={(event) => form.setValue("highRiskEthnicity", event.target.checked)}
                />
                {"Ethnicity"}
              </label>
              {(isPregnancyConditionVisible(patient) ||
                !!data?.historyOfGestationalDiabetes) && (
                <label className={style.pregnancyFormCheckboxLabel}>
                  <CustomCheckbox
                    checked={form.watch("historyOfGestationalDiabetes")}
                    onChange={(event) => form.setValue("historyOfGestationalDiabetes", event.target.checked)}
                  />
                  {"History of Gestational Diabetes"}
                </label>
              )}
              <label className={style.pregnancyFormCheckboxLabel}>
                <CustomCheckbox
                  checked={form.watch("dyslipidsemia")}
                  onChange={(event) => form.setValue("dyslipidsemia", event.target.checked)}
                />
                {"Dyslipidaemia (previously recorded)"}
              </label>
              <label className={style.pregnancyFormCheckboxLabel}>
                <CustomCheckbox
                  checked={form.watch("chronicKidneyDisease")}
                  onChange={(event) => form.setValue("chronicKidneyDisease", event.target.checked)}
                />
                {"Moderate or severe chronic kidney disease (eGFR <60ml/min 1.73m2 previously recorded)"}
              </label>
              <label className={style.pregnancyFormCheckboxLabel}>
                <CustomCheckbox
                  checked={form.watch("historyOfSevereMentalIllness")}
                  onChange={(event) => form.setValue("historyOfSevereMentalIllness", event.target.checked)}
                />
                History of severe Mental Illness
              </label>
              <label className={style.pregnancyFormCheckboxLabel}>
                <CustomCheckbox
                  checked={form.watch("other")}
                  onChange={(event) => form.setValue("other", event.target.checked)}
                />
                Other
              </label>
            </div>
          </fieldset>
          <FormField name="otherText">
            <textarea
              className="form-control textareaform-control"
              disabled={!form.watch("other")}
              style={{ minHeight: "100px", marginTop: "16px" }}
              {...form.register("otherText")}
            />
          </FormField>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: "20px", padding: "24px", borderTop: "1px solid var(--gray-200)" }}>
        <Button variant="outline" type="button" onClick={onCancel} style={{ width: "200px" }}>Back</Button>
        <Button type="submit" disabled={submitDisabled} style={{ width: "200px" }}>Continue</Button>
      </div>
      </form>
    </FormProvider>
  );
};
